
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Pencil, Eraser, Minus, ArrowRight, Square, Circle, Undo, Redo, Trash2, Save, Palette, X, ChevronDown, Plus, Copy, ZoomIn, ZoomOut, Hand, Maximize2, Minimize2, ArrowLeft } from 'lucide-react';

interface SketchPadProps {
  initialImage: string | null | undefined;
  onSave: (base64: string | null, saveAsNew?: boolean) => void;
  isEditingExisting?: boolean;
  onBack?: () => void;
}

type ToolType = 'pen' | 'eraser' | 'line' | 'arrow' | 'rect' | 'circle' | 'pan';

const SKETCH_COLORS = [
    '#000000', '#374151', '#9ca3af', '#ffffff',
    '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#22c55e', '#06b6d4', '#3b82f6', '#8b5cf6', '#d946ef', '#f43f5e',
    '#fecaca', '#fed7aa', '#fde68a', '#d9f99d', '#bbf7d0', '#a5f3fc', '#bfdbfe', '#ddd6fe', '#f5d0fe', '#fecdd3'
];

const LINE_WIDTHS = [2, 4, 6, 8, 12, 16];
const DEFAULT_BLANK_W = 2000;
const DEFAULT_BLANK_H = 1500;

export const SketchPad: React.FC<SketchPadProps> = ({ initialImage, onSave, isEditingExisting = false, onBack }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);

  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState<ToolType>('pen');
  const [color, setColor] = useState('#000000');
  const [lineWidth, setLineWidth] = useState(4);
  const [showSaveMenu, setShowSaveMenu] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [customColors, setCustomColors] = useState<string[]>([]);
  const [history, setHistory] = useState<ImageData[]>([]);
  const [historyStep, setHistoryStep] = useState(-1);
  const startPos = useRef<{x: number, y: number} | null>(null);

  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const zoomRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });
  const isPanningRef = useRef(false);
  const panStartRef = useRef({ mx: 0, my: 0, px: 0, py: 0 });
  const [isFocused, setIsFocused] = useState(false);

  const historyRef = useRef<ImageData[]>([]);
  const historyStepRef = useRef(-1);

  const applyZoom = useCallback((z: number) => { zoomRef.current = z; setZoom(z); }, []);
  const applyPan = useCallback((p: { x: number; y: number }) => { panRef.current = p; setPan(p); }, []);
  const resetView = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const { width: cW, height: cH } = container.getBoundingClientRect();
    const fitZoom = Math.min(cW / canvas.width, cH / canvas.height) * 0.95;
    applyZoom(fitZoom);
    applyPan({ x: (cW - canvas.width * fitZoom) / 2, y: (cH - canvas.height * fitZoom) / 2 });
  }, [applyZoom, applyPan]);

  const saveHistory = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const newHistory = historyRef.current.slice(0, historyStepRef.current + 1);
    newHistory.push(imageData);
    historyRef.current = newHistory;
    historyStepRef.current = newHistory.length - 1;
    setHistory([...newHistory]);
    setHistoryStep(newHistory.length - 1);
  }, []);

  const initCanvas = useCallback((w: number, h: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h);
    }
  }, []);

  const fitToContainer = useCallback((imgW: number, imgH: number) => {
    const container = containerRef.current;
    if (!container) return;
    const { width: cW, height: cH } = container.getBoundingClientRect();
    const fitZoom = Math.min(cW / imgW, cH / imgH) * 0.92;
    applyZoom(fitZoom);
    applyPan({ x: (cW - imgW * fitZoom) / 2, y: (cH - imgH * fitZoom) / 2 });
  }, [applyZoom, applyPan]);

  // Initialize canvas on mount
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    if (initialImage) {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      const drawImage = (source: HTMLImageElement) => {
        initCanvas(source.naturalWidth, source.naturalHeight);
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(source, 0, 0);
        fitToContainer(source.naturalWidth, source.naturalHeight);
        saveHistory();
      };

      img.onload = () => drawImage(img);
      img.onerror = () => {
        // CORS failed — retry without crossOrigin (display only, taint accepted)
        const img2 = new Image();
        img2.onload = () => drawImage(img2);
        img2.onerror = () => {
          // Image completely failed — init blank canvas
          const { width, height } = container.getBoundingClientRect();
          initCanvas(Math.max(width, DEFAULT_BLANK_W), Math.max(height, DEFAULT_BLANK_H));
          fitToContainer(canvas.width, canvas.height);
          saveHistory();
        };
        img2.src = initialImage;
      };
      img.src = initialImage;
    } else {
      const { width, height } = container.getBoundingClientRect();
      initCanvas(Math.max(width, DEFAULT_BLANK_W), Math.max(height, DEFAULT_BLANK_H));
      fitToContainer(canvas.width, canvas.height);
      saveHistory();
    }

    try {
      const saved = localStorage.getItem('ms_custom_colors');
      if (saved) setCustomColors(JSON.parse(saved));
    } catch (e) {}

    // Wheel zoom
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = container.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
      const newZoom = Math.max(0.05, Math.min(8, zoomRef.current * factor));
      const newPan = {
        x: mouseX - (mouseX - panRef.current.x) * (newZoom / zoomRef.current),
        y: mouseY - (mouseY - panRef.current.y) * (newZoom / zoomRef.current),
      };
      zoomRef.current = newZoom;
      panRef.current = newPan;
      setZoom(newZoom);
      setPan(newPan);
    };
    container.addEventListener('wheel', onWheel, { passive: false });
    return () => container.removeEventListener('wheel', onWheel);
  }, []);

  // Intercept Ctrl+Z / Ctrl+Shift+Z in capture phase so App.tsx global undo never fires
  // while SketchPad is mounted. Without this, Ctrl+Z would silently undo the last mind-map
  // edit instead of undoing a drawing stroke.
  useEffect(() => {
    const handleUndoRedo = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        e.stopImmediatePropagation();
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;
        if (e.shiftKey) {
          if (historyStepRef.current < historyRef.current.length - 1) {
            const newStep = historyStepRef.current + 1;
            historyStepRef.current = newStep;
            setHistoryStep(newStep);
            ctx.putImageData(historyRef.current[newStep], 0, 0);
          }
        } else {
          if (historyStepRef.current > 0) {
            const newStep = historyStepRef.current - 1;
            historyStepRef.current = newStep;
            setHistoryStep(newStep);
            ctx.putImageData(historyRef.current[newStep], 0, 0);
          }
        }
      }
    };
    window.addEventListener('keydown', handleUndoRedo, { capture: true });
    return () => window.removeEventListener('keydown', handleUndoRedo, { capture: true });
  }, []);

  const saveCustomColor = (newColor: string) => {
    const newColors = [newColor, ...customColors.filter(c => c !== newColor)].slice(0, 12);
    setCustomColors(newColors);
    localStorage.setItem('ms_custom_colors', JSON.stringify(newColors));
  };

  const handleColorPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    setColor(e.target.value);
    saveCustomColor(e.target.value);
  };

  const handleUndo = () => {
    if (historyStepRef.current > 0) {
      const newStep = historyStepRef.current - 1;
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;
      ctx.putImageData(historyRef.current[newStep], 0, 0);
      historyStepRef.current = newStep;
      setHistoryStep(newStep);
    }
  };

  const handleRedo = () => {
    if (historyStepRef.current < historyRef.current.length - 1) {
      const newStep = historyStepRef.current + 1;
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;
      ctx.putImageData(historyRef.current[newStep], 0, 0);
      historyStepRef.current = newStep;
      setHistoryStep(newStep);
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    saveHistory();
  };

  // Map screen coords → canvas pixel coords accounting for CSS zoom/pan transform
  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
    if (tool === 'pan' || ('button' in e && e.button === 1)) {
      if ('button' in e && e.button === 1) e.preventDefault();
      isPanningRef.current = true;
      panStartRef.current = { mx: clientX, my: clientY, px: panRef.current.x, py: panRef.current.y };
      return;
    }
    setIsDrawing(true);
    const { x, y } = getPos(e);
    startPos.current = { x, y };
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (isPanningRef.current) {
      const cx = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
      const cy = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
      applyPan({ x: panStartRef.current.px + (cx - panStartRef.current.mx), y: panStartRef.current.py + (cy - panStartRef.current.my) });
      return;
    }
    if (!isDrawing) return;
    const { x, y } = getPos(e);
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !startPos.current) return;

    if (tool === 'pen' || tool === 'eraser') {
      ctx.globalCompositeOperation = tool === 'eraser' ? 'destination-out' : 'source-over';
      ctx.lineTo(x, y);
      ctx.stroke();
    } else {
      const lastImageData = historyRef.current[historyStepRef.current];
      if (lastImageData) ctx.putImageData(lastImageData, 0, 0);
      ctx.beginPath();
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      const sx = startPos.current.x;
      const sy = startPos.current.y;

      if (tool === 'line') {
        ctx.moveTo(sx, sy); ctx.lineTo(x, y);
      } else if (tool === 'arrow') {
        ctx.moveTo(sx, sy); ctx.lineTo(x, y);
        const headLen = lineWidth * 3;
        const angle = Math.atan2(y - sy, x - sx);
        ctx.lineTo(x - headLen * Math.cos(angle - Math.PI / 6), y - headLen * Math.sin(angle - Math.PI / 6));
        ctx.moveTo(x, y);
        ctx.lineTo(x - headLen * Math.cos(angle + Math.PI / 6), y - headLen * Math.sin(angle + Math.PI / 6));
      } else if (tool === 'rect') {
        ctx.rect(sx, sy, x - sx, y - sy);
      } else if (tool === 'circle') {
        const radius = Math.sqrt(Math.pow(x - sx, 2) + Math.pow(y - sy, 2));
        ctx.arc(sx, sy, radius, 0, 2 * Math.PI);
      }
      ctx.stroke();
    }
  };

  const stopDrawing = () => {
    if (isPanningRef.current) { isPanningRef.current = false; return; }
    if (!isDrawing) return;
    setIsDrawing(false);
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) ctx.closePath();
    saveHistory();
  };

  const handleSaveCanvas = (saveAsNew: boolean = false) => {
    if (!canvasRef.current) return;
    const blank = document.createElement('canvas');
    blank.width = canvasRef.current.width;
    blank.height = canvasRef.current.height;
    if (canvasRef.current.toDataURL() === blank.toDataURL()) {
      onSave(null, saveAsNew);
    } else {
      onSave(canvasRef.current.toDataURL('image/png'), saveAsNew);
    }
    setShowSaveMenu(false);
  };

  return (
    <div className={isFocused
      ? 'fixed inset-0 z-[9999] flex flex-col bg-gray-100 dark:bg-slate-900'
      : 'flex flex-col h-full bg-gray-100 dark:bg-slate-900 rounded-xl overflow-hidden'
    }>
      {/* Toolbar */}
      <div className="p-3 bg-white dark:bg-slate-800 border-b dark:border-slate-700 flex flex-wrap items-center gap-4 justify-between relative z-20 shrink-0">
        {/* Back + Tools */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {onBack && (
            <>
              <button
                onClick={onBack}
                className="flex items-center gap-1 px-2 py-1.5 text-sm text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-md transition-colors"
                title="Quay lại"
              >
                <ArrowLeft size={16} />
                <span className="text-xs font-medium">Quay lại</span>
              </button>
              <div className="w-px h-5 bg-gray-300 dark:bg-slate-600" />
            </>
          )}
          <div className="flex items-center gap-1 bg-gray-100 dark:bg-slate-900 p-1 rounded-lg">
          <button onClick={() => setTool('pan')} className={`p-2 rounded-md transition-colors ${tool === 'pan' ? 'bg-white dark:bg-slate-700 shadow text-blue-600' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`} title="Di chuyển ảnh (Hand tool)">
            <Hand size={18} />
          </button>
          <div className="w-px h-5 bg-gray-300 dark:bg-slate-600 mx-0.5" />
          {([
            { id: 'pen', icon: Pencil },
            { id: 'eraser', icon: Eraser },
            { id: 'line', icon: Minus },
            { id: 'arrow', icon: ArrowRight },
            { id: 'rect', icon: Square },
            { id: 'circle', icon: Circle }
          ] as const).map((t) => (
            <button key={t.id} onClick={() => setTool(t.id)} className={`p-2 rounded-md transition-colors ${tool === t.id ? 'bg-white dark:bg-slate-700 shadow text-blue-600' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`} title={t.id}>
              <t.icon size={18} />
            </button>
          ))}
          </div>
        </div>

        {/* Color Picker — compact popover */}
        <div className="relative flex-shrink-0">
          <button
            onClick={() => setShowColorPicker(p => !p)}
            className={`w-8 h-8 rounded-full border-2 transition-all shadow-sm ${showColorPicker ? 'border-blue-500 scale-110' : 'border-gray-300 dark:border-slate-500 hover:border-blue-400'}`}
            style={{ backgroundColor: color }}
            title="Chọn màu"
          />
          {showColorPicker && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setShowColorPicker(false)} />
              <div className="absolute top-full left-0 mt-2 z-40 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-xl p-3 min-w-max">
                <div className="grid grid-cols-6 gap-1.5">
                  {SKETCH_COLORS.map(c => (
                    <button
                      key={c}
                      onClick={() => { setColor(c); setShowColorPicker(false); }}
                      className={`w-7 h-7 rounded-full border border-gray-200 dark:border-slate-600 transition-transform hover:scale-110 ${color === c ? 'ring-2 ring-blue-500 scale-110' : ''}`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
                {customColors.length > 0 && (
                  <>
                    <div className="h-px bg-gray-200 dark:bg-slate-700 my-2" />
                    <div className="grid grid-cols-6 gap-1.5">
                      {customColors.map((c, i) => (
                        <button
                          key={i}
                          onClick={() => { setColor(c); setShowColorPicker(false); }}
                          className={`w-7 h-7 rounded-full border border-gray-200 dark:border-slate-600 transition-transform hover:scale-110 ${color === c ? 'ring-2 ring-blue-500 scale-110' : ''}`}
                          style={{ backgroundColor: c }}
                        />
                      ))}
                    </div>
                  </>
                )}
                <div className="h-px bg-gray-200 dark:bg-slate-700 my-2" />
                <div className="relative">
                  <button
                    onClick={() => colorInputRef.current?.click()}
                    className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors w-full px-1 py-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700"
                  >
                    <Plus size={13} />
                    <span>Màu tùy chỉnh</span>
                  </button>
                  <input ref={colorInputRef} type="color" className="absolute inset-0 opacity-0 cursor-pointer w-full h-full" onChange={handleColorPick} value={color} />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Line Width */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {LINE_WIDTHS.map(w => (
            <button key={w} onClick={() => setLineWidth(w)} className={`w-8 h-8 flex items-center justify-center rounded-md ${lineWidth === w ? 'bg-gray-200 dark:bg-slate-700' : ''}`}>
              <div className="bg-gray-800 dark:bg-gray-200 rounded-full" style={{ width: w / 1.5, height: w / 1.5 }} />
            </button>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={handleUndo} disabled={historyStep <= 0} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-md disabled:opacity-30"><Undo size={18} /></button>
          <button onClick={handleRedo} disabled={historyStep >= history.length - 1} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-md disabled:opacity-30"><Redo size={18} /></button>
          <div className="h-6 w-px bg-gray-300 dark:bg-slate-600 mx-1" />
          <button onClick={() => { const z = Math.min(8, zoomRef.current * 1.25); applyZoom(z); }} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-md" title="Zoom in"><ZoomIn size={16} /></button>
          <button onClick={resetView} className="px-2 py-1 text-xs font-mono text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-md min-w-[44px] text-center" title="Reset view">{Math.round(zoom * 100)}%</button>
          <button onClick={() => { const z = Math.max(0.05, zoomRef.current / 1.25); applyZoom(z); }} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-md" title="Zoom out"><ZoomOut size={16} /></button>
          <div className="h-6 w-px bg-gray-300 dark:bg-slate-600 mx-1" />
          <button onClick={clearCanvas} className="p-2 hover:bg-red-50 text-red-500 rounded-md" title="Clear All"><Trash2 size={18} /></button>
          <div className="h-6 w-px bg-gray-300 dark:bg-slate-600 mx-1" />
          <button onClick={() => setIsFocused(f => !f)} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-md text-gray-500" title={isFocused ? 'Thu gọn' : 'Toàn màn hình'}>
            {isFocused ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
          {isEditingExisting ? (
            <div className="relative ml-2">
              <button onClick={() => setShowSaveMenu(!showSaveMenu)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-bold text-sm">
                <Save size={16} /> Save <ChevronDown size={14} />
              </button>
              {showSaveMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-slate-700 z-50">
                  <button onClick={() => handleSaveCanvas(false)} className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-slate-700 flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-slate-600">
                    <Save size={14} /> Save → thay thế ảnh gốc
                  </button>
                  <button onClick={() => handleSaveCanvas(true)} className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-slate-700 flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Copy size={14} /> Save as New → giữ ảnh gốc
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button onClick={() => handleSaveCanvas(false)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-bold text-sm ml-2">
              <Save size={16} /> Save
            </button>
          )}
        </div>
      </div>

      {/* Canvas Area */}
      <div
        ref={containerRef}
        className="flex-1 relative overflow-hidden touch-none select-none"
        style={{ cursor: isPanningRef.current ? 'grabbing' : tool === 'pan' ? 'grab' : 'crosshair', background: '#e5e7eb' }}
      >
        {/* Zoom/pan viewport — transforms the canvas */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: '0 0',
            boxShadow: '0 4px 32px rgba(0,0,0,0.18)',
          }}
        >
          <canvas
            ref={canvasRef}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            style={{ display: 'block' }}
          />
        </div>
      </div>
    </div>
  );
};
